import React, { useState } from 'react';
import {
  Cpu, Brain, Code, Server, Database, Smartphone, Cloud, ShieldCheck, Zap,
  CheckCircle2, ArrowRight, Sparkles, Layers, Bot, Network, Lock, PhoneCall, X, User, Phone, Mail, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const TechnologiesPage = () => {
  const [selectedTech, setSelectedTech] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: 'Generative AI & LLM App',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  const aiStack = [
    {
      category: 'LLMs & Foundation Models',
      badge: 'GEN AI & AGENTS',
      badgeColor: 'from-purple-500 to-indigo-600',
      description: 'Custom fine-tuning, prompt engineering, and API integration with state-of-the-art AI reasoning models.',
      items: [
        { name: 'OpenAI GPT-4o / O3', desc: 'Multimodal reasoning & vision LLMs' },
        { name: 'Claude 3.5 Sonnet', desc: 'Superior coding & long-context analysis' },
        { name: 'Llama 3.3 & DeepSeek R1', desc: 'Open-weight enterprise models' },
        { name: 'Mistral Large & Codestral', desc: 'High-speed European LLM engines' }
      ]
    },
    {
      category: 'AI Orchestration & Agents',
      badge: 'AUTONOMOUS WORKFLOWS',
      badgeColor: 'from-cyan-500 to-blue-600',
      description: 'Building multi-agent task execution networks with tool calling, memory state, and human-in-the-loop controls.',
      items: [
        { name: 'LangChain & LangGraph', desc: 'Stateful multi-agent workflows' },
        { name: 'LlamaIndex', desc: 'Advanced RAG data indexing pipelines' },
        { name: 'CrewAI & AutoGen', desc: 'Collaborative autonomous agent teams' },
        { name: 'Semantic Kernel', desc: 'Enterprise AI orchestration framework' }
      ]
    },
    {
      category: 'Vector DBs & Semantic Search',
      badge: 'RAG ARCHITECTURE',
      badgeColor: 'from-emerald-500 to-teal-600',
      description: 'Ultra-fast vector embedding storage for enterprise knowledge retrieval and semantic search.',
      items: [
        { name: 'Pinecone & Qdrant', desc: 'High-scale cloud vector search' },
        { name: 'Milvus & ChromaDB', desc: 'Open-source vector similarity search' },
        { name: 'pgvector (PostgreSQL)', desc: 'Relational + vector hybrid database' },
        { name: 'Hugging Face Embeddings', desc: 'Custom domain embedding models' }
      ]
    },
    {
      category: 'Deep Learning & Edge AI',
      badge: 'MODEL TRAINING',
      badgeColor: 'from-orange-500 to-amber-600',
      description: 'Model quantization, LoRA fine-tuning, and low-latency edge deployment for privacy-first AI.',
      items: [
        { name: 'PyTorch & TensorFlow', desc: 'Model architecture & neural nets' },
        { name: 'Ollama & vLLM', desc: 'High-throughput local LLM inference' },
        { name: 'TensorRT & ONNX', desc: 'GPU-accelerated inference optimization' },
        { name: 'Whisper AI & Stable Diffusion', desc: 'Speech-to-text & generative vision' }
      ]
    }
  ];

  const frameworkCategories = [
    {
      id: 'web',
      title: 'Modern Web Frameworks',
      icon: Code,
      color: 'text-cyan-600 bg-cyan-50 border-cyan-200',
      techs: [
        { name: 'Next.js 15', tag: 'React App Framework' },
        { name: 'React 19', tag: 'UI Library' },
        { name: 'TypeScript 5', tag: 'Type Safety' },
        { name: 'Vite', tag: 'Next-Gen Bundler' },
        { name: 'Tailwind CSS', tag: 'Design Engine' },
        { name: 'Vue 3 & Nuxt', tag: 'Modern Web' }
      ]
    },
    {
      id: 'backend',
      title: 'Backend & High-Throughput APIs',
      icon: Server,
      color: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      techs: [
        { name: 'Node.js & Express', tag: 'JavaScript Engine' },
        { name: 'Python FastAPI', tag: 'Async Microservices' },
        { name: 'Go (Golang)', tag: 'High Concurrency' },
        { name: 'Rust', tag: 'Memory-Safe Systems' },
        { name: 'NestJS', tag: 'Enterprise Node' },
        { name: 'GraphQL & gRPC', tag: 'High-Speed Protocols' }
      ]
    },
    {
      id: 'database',
      title: 'Databases & In-Memory Cache',
      icon: Database,
      color: 'text-blue-600 bg-blue-50 border-blue-200',
      techs: [
        { name: 'PostgreSQL', tag: 'Relational DB' },
        { name: 'Supabase', tag: 'Open-Source Firebase' },
        { name: 'MongoDB', tag: 'Document Store' },
        { name: 'Redis', tag: 'In-Memory Cache' },
        { name: 'ClickHouse', tag: 'Real-Time Analytics' },
        { name: 'Neo4j', tag: 'Graph Database' }
      ]
    },
    {
      id: 'mobile',
      title: 'Cross-Platform Mobile Apps',
      icon: Smartphone,
      color: 'text-purple-600 bg-purple-50 border-purple-200',
      techs: [
        { name: 'React Native', tag: 'iOS & Android' },
        { name: 'Flutter', tag: 'Google Dart UI' },
        { name: 'Expo', tag: 'Mobile Ecosystem' },
        { name: 'Swift (iOS)', tag: 'Native Apple' },
        { name: 'Kotlin (Android)', tag: 'Native Android' },
        { name: 'PWA', tag: 'Progressive Web' }
      ]
    },
    {
      id: 'cloud',
      title: 'Multi-Cloud & DevOps Infrastructure',
      icon: Cloud,
      color: 'text-orange-600 bg-orange-50 border-orange-200',
      techs: [
        { name: 'AWS Cloud', tag: 'Global Cloud' },
        { name: 'Google Cloud Platform', tag: 'AI & Data Cloud' },
        { name: 'Docker', tag: 'Containerization' },
        { name: 'Kubernetes (K8s)', tag: 'Container Orchestration' },
        { name: 'Vercel Enterprise', tag: 'Edge Hosting' },
        { name: 'GitHub Actions', tag: 'CI/CD Automation' }
      ]
    }
  ];

  const handleOpenModal = (techName) => {
    setSelectedTech(techName);
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitRequest = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error('Please enter your name and mobile number');
      return;
    }

    const targetTech = selectedTech || 'Enterprise Tech Stack';
    const messageText = `Hello HOASP International Team,%0A%0A*Technology Stack Consultation Request*%0A- *Target Stack:* ${targetTech}%0A- *Client Name:* ${formData.name}%0A- *Mobile:* ${formData.phone}%0A- *Email:* ${formData.email || 'N/A'}%0A- *Project Type:* ${formData.projectType}%0A- *Details:* ${formData.message || 'Call me back for tech architecture review.'}`;

    const whatsappUrl = `https://wa.me/918796118101?text=${messageText}`;
    const mailtoUrl = `mailto:info@hoasp.com?subject=Tech Consultation: ${encodeURIComponent(targetTech)} - ${encodeURIComponent(formData.name)}&body=${decodeURIComponent(messageText).replace(/%0A/g, '%0D%0A')}`;

    window.open(whatsappUrl, '_blank');

    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 500);

    setIsSubmitted(true);
    toast.success(`Consultation request submitted for ${targetTech}!`);
  };

  return (
    <div className="min-h-screen bg-gray-950 pt-20 text-left text-gray-100 font-['Roboto',sans-serif]">
      {/* Dark Glowing Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-900 via-slate-900 to-gray-950 border-b border-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b40_1px,transparent_1px),linear-gradient(to_bottom,#1e293b40_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-gradient-to-tr from-cyan-500/20 via-orange-500/20 to-transparent rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
              <Cpu className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-300 text-xs md:text-sm font-semibold tracking-wide uppercase">Enterprise Tech Stack & AI Frameworks</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
              Powered by Next-Gen <br />
              <span className="bg-gradient-to-r from-cyan-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">AI & Modern Frameworks</span>
            </h1>

            <p className="text-gray-300 text-base md:text-xl mb-8 leading-relaxed max-w-3xl mx-auto">
              From Generative AI models and vector databases to high-throughput web applications, cloud microservices, and cross-platform mobile apps.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleOpenModal('AI & Modern Tech Stack Consultation')}
                className="bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-500 hover:from-orange-600 hover:to-cyan-600 text-white px-8 py-4 text-base font-bold rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
              >
                Schedule Tech Architecture Review <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* WHITE STRIP SECTION 1: "Generative AI & LLM Machine Learning Stack" */}
      <section className="py-20 bg-slate-50 border-t border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-purple-100 border border-purple-200 rounded-full px-4 py-1 mb-3">
              <Brain className="w-4 h-4 text-purple-600" />
              <span className="text-purple-700 text-xs font-bold uppercase tracking-wider">AI & Machine Learning Matrix</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight mt-1 mb-4">
              Generative AI & Intelligent Agent Stack
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto leading-relaxed">
              We integrate enterprise LLMs, fine-tuned RAG vector pipelines, and autonomous AI agents directly into your software.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {aiStack.map((aiCategory, idx) => (
              <div
                key={idx}
                className="bg-white border border-gray-200 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 relative overflow-hidden group"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-[11px] font-black uppercase tracking-wider text-white bg-gradient-to-r ${aiCategory.badgeColor} px-3 py-1 rounded-full shadow-sm`}>
                    {aiCategory.badge}
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{aiCategory.category}</h3>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed min-h-[40px]">{aiCategory.description}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-gray-200">
                  {aiCategory.items.map((item, itemIdx) => (
                    <div
                      key={itemIdx}
                      className="bg-slate-50 border border-gray-200 p-3.5 rounded-xl hover:border-cyan-500/50 hover:bg-cyan-50/40 transition-colors"
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <span className="text-sm font-bold text-gray-900">{item.name}</span>
                      </div>
                      <span className="text-xs text-gray-500 block pl-6">{item.desc}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => handleOpenModal(aiCategory.category)}
                  className="w-full mt-6 bg-gray-900 hover:bg-black text-white font-bold py-3 rounded-xl transition-all shadow-md"
                >
                  Consult on {aiCategory.category}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHITE STRIP SECTION 2: "Modern Web, Mobile & Cloud Engineering" */}
      <section className="py-20 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-600 text-sm font-bold uppercase tracking-wider">Production-Grade Frameworks</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mt-2 mb-4">
              Modern Frontend, Backend & Cloud Architecture
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
              Clean code architectures built for high concurrency, zero downtime, and seamless cross-platform experiences.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {frameworkCategories.map((cat) => (
              <div
                key={cat.id}
                className="bg-slate-50 border border-gray-200 rounded-3xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200">
                    <div className={`p-3 rounded-xl border ${cat.color}`}>
                      <cat.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{cat.title}</h3>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {cat.techs.map((tech, idx) => (
                      <div
                        key={idx}
                        className="bg-white border border-gray-200 p-3 rounded-xl shadow-xs hover:border-orange-500/50 transition-all text-left"
                      >
                        <span className="text-sm font-bold text-gray-900 block">{tech.name}</span>
                        <span className="text-[11px] text-gray-500 font-medium">{tech.tag}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={() => handleOpenModal(cat.title)}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl transition-colors"
                >
                  Explore {cat.title}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHITE STRIP SECTION 3: "Enterprise AI Architecture Standards" */}
      <section className="py-20 bg-slate-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-cyan-600 text-sm font-bold uppercase tracking-wider">Engineering Rigor</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-2 mb-6">
                Why Global Brands Choose HOASP Tech Stack
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
                We combine battle-tested cloud security, modular microservices, and AI guardrails to deliver scalable web platforms with zero technical debt.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-3 bg-white border border-gray-200 p-4 rounded-2xl shadow-sm">
                  <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-base font-bold text-gray-900">Zero-Trust AI Guardrails & Privacy</h4>
                    <p className="text-sm text-gray-600">Enterprise data isolation with custom PII scrubbing and NDA compliance.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-white border border-gray-200 p-4 rounded-2xl shadow-sm">
                  <Zap className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-base font-bold text-gray-900">Sub-100ms API Response SLA</h4>
                    <p className="text-sm text-gray-600">Optimized database indexing, Redis caching, and HTTP/3 QUIC edge acceleration.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-white border border-gray-200 p-4 rounded-2xl shadow-sm">
                  <Lock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-base font-bold text-gray-900">100% IP & Source Code Ownership</h4>
                    <p className="text-sm text-gray-600">Full git repository transfer, complete documentation, and zero vendor lock-in.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-3xl p-8 md:p-10 shadow-xl relative overflow-hidden">
              <Bot className="w-14 h-14 text-cyan-600 mb-6" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Need Custom AI Model Integration or Tech Stack Migration?</h3>
              <p className="text-gray-600 mb-8 leading-relaxed text-sm md:text-base">
                Our lead software architects offer 1-on-1 technical reviews for modernizing legacy codebases or building AI agent solutions.
              </p>

              <Button
                onClick={() => handleOpenModal('Full Technology Stack Migration')}
                className="w-full bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 hover:from-cyan-700 hover:to-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-cyan-500/20"
              >
                Schedule Free Tech Architecture Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* REQUEST CONSULTATION MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 rounded-3xl max-w-lg w-full p-6 md:p-8 shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-200 text-left">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-5 right-5 text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center space-x-4 mb-6 pb-4 border-b border-gray-200">
              <div className="w-12 h-12 bg-cyan-100 border border-cyan-200 rounded-xl flex items-center justify-center text-cyan-600 flex-shrink-0">
                <Cpu className="w-6 h-6" />
              </div>
              <div>
                <span className="text-orange-600 text-xs font-semibold uppercase tracking-wider flex items-center gap-1">
                  <PhoneCall className="w-3.5 h-3.5" /> Tech Stack Consultation
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900">{selectedTech}</h3>
              </div>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 border border-emerald-300 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">Request Transmitted!</h4>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Your request for <strong className="text-cyan-600">{selectedTech}</strong> has been transmitted to our software architecture team via WhatsApp and email.
                </p>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3.5 rounded-xl"
                >
                  Close & Return to Page
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmitRequest} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Your Full Name *</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Vikram Verma"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Phone Number *</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="email"
                        name="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Project / Tech Category</label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                  >
                    <option value="Generative AI & LLM App">Generative AI & LLM App</option>
                    <option value="Autonomous AI Agent Team">Autonomous AI Agent Team</option>
                    <option value="RAG Vector Search System">RAG Vector Search System</option>
                    <option value="Full-Stack Web App (Next.js / React)">Full-Stack Web App (Next.js / React)</option>
                    <option value="Mobile App (React Native / Flutter)">Mobile App (React Native / Flutter)</option>
                    <option value="Cloud Migration & Microservices">Cloud Migration & Microservices</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Project Notes / Requirements</label>
                  <textarea
                    name="message"
                    rows="3"
                    placeholder="Describe your target stack, LLM requirements, or cloud architecture..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                  ></textarea>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-600 hover:from-orange-600 hover:to-cyan-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Submit Request to Mobile & Email
                </Button>

                <p className="text-[11px] text-gray-500 text-center mt-2">
                  🔒 We respect your privacy. Request sent directly to mobile (+91 8796 118 101) & email.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnologiesPage;
